import { authFetch, openAuthPdf } from '@/lib/api';
import PrintButton from '@/components/PrintButton';
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
    Loader2, ChevronLeft, ChevronRight, Send, FileDown, Plus, Trash2, Save, ArrowLeft, FlaskConical, PackageCheck, MessageCircle, Search, X, Sparkles
} from 'lucide-react';
import ConsolidatedAIImportDialog from '@/components/ConsolidatedAIImportDialog';
import ConsolidatedReceptionDialog from '@/components/ConsolidatedReceptionDialog';
import OrderableModeToggle from '@/pages/orders/components/OrderableModeToggle';
import WhatsAppShareDialog from '@/pages/orders/dialogs/WhatsAppShareDialog';
import { getStatusClass, getStatusLabel } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Plage de dates (lundi → dimanche) d'une semaine ISO "2026-W33" — même calcul que le backend (basé sur le 4 janvier).
const weekDateRange = (weekStr) => {
    const y = parseInt((weekStr || '').slice(0, 4), 10);
    const w = parseInt((weekStr || '').split('W')[1], 10);
    if (!y || !w) return '';
    const jan4 = new Date(Date.UTC(y, 0, 4));
    const monday = new Date(jan4);
    monday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (w - 1) * 7);
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    const fmt = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
    return `${fmt(monday)} - ${fmt(sunday)}`;
};

function WeekSelector({ week, onChange }) {
    const shift = (dir) => {
        const [y, w] = [parseInt(week.slice(0, 4)), parseInt(week.split('W')[1])];
        let nw = w + dir, ny = y;
        if (nw < 1) { ny--; nw = 52; }
        if (nw > 52) { ny++; nw = 1; }
        onChange(`${ny}-W${String(nw).padStart(2, '0')}`);
    };
    const label = `Semaine ${week.split('W')[1]} / ${week.slice(0, 4)}`;
    return (
        <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-semibold text-slate-700 min-w-[130px] text-center">{label}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
    );
}

function OverviewPanel({ week, onSelectSupplier, suppliers }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [supplierList, setSupplierList] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [overRes, supRes] = await Promise.all([
                    authFetch(`${API}/api/consolidation/overview?week=${week}`),
                    authFetch(`${API}/api/suppliers`),
                ]);
                const overview = await overRes.json();
                const allSup = await supRes.json();
                setData(overview);
                setSupplierList(allSup);
            } catch { toast.error('Erreur chargement'); }
            finally { setLoading(false); }
        })();
    }, [week]);

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

    const existingIds = (data?.suppliers || []).map(s => s.supplier_id);
    const withOrders = data?.suppliers || [];
    const withoutOrders = supplierList
        .filter(s => !existingIds.includes(s.id))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }));

    // Unique statuses present in this week to build a relevant legend.
    const STATUS_ORDER = ['pending', 'confirmed', 'shipped', 'partially_received', 'delivered', 'cancelled'];
    const statusesPresent = Array.from(new Set(
        withOrders.flatMap(s => Object.values(s.sites).map(site => site.status))
    )).sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b));

    return (
        <div className="space-y-3" data-testid="consolidation-overview">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs text-slate-500">
                    {data?.week_start} - {data?.week_end}
                </p>
                <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap" data-testid="consolidation-legend">
                    {statusesPresent.map(status => (
                        <span
                            key={status}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusClass(status)}`}
                        >
                            {getStatusLabel(status)}
                        </span>
                    ))}
                </div>
            </div>
            {withOrders.length === 0 && withoutOrders.length > 0 && (
                <p className="text-sm text-slate-400 text-center py-4">Aucune commande de site pour cette semaine</p>
            )}
            {withOrders.map(s => (
                <button
                    key={s.supplier_id}
                    onClick={() => onSelectSupplier(s.supplier_id)}
                    className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-orange-300 hover:shadow-sm transition-all"
                    data-testid={`supplier-${s.supplier_id}`}
                >
                    <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm text-slate-800">{s.supplier_name}</span>
                        <span className="text-xs font-medium text-orange-600">{s.total_amount.toFixed(0)} EUR</span>
                    </div>
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        {Object.values(s.sites).map((site, i) => (
                            <span
                                key={i}
                                className={`text-xs px-1.5 py-0.5 rounded-full ${getStatusClass(site.status)}`}
                                title={getStatusLabel(site.status)}
                            >{site.site_name.replace('FE ', '')}</span>
                        ))}
                    </div>
                </button>
            ))}

            {withoutOrders.length > 0 && (
                <>
                    <p className="text-xs text-slate-400 pt-2">Autres fournisseurs (aucune commande) :</p>
                    <div className="grid grid-cols-2 gap-2">
                        {withoutOrders.map(s => (
                            <button
                                key={s.id}
                                onClick={() => onSelectSupplier(s.id)}
                                className="text-left text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 hover:border-orange-300 transition-all text-slate-500"
                            >
                                {s.name}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function DetailPanel({ supplierId, week, onBack, config }) {
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [products, setProducts] = useState([]);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [showAddProduct, setShowAddProduct] = useState(false);
    const [showReceptionDialog, setShowReceptionDialog] = useState(false);
    const [receptionEditMode, setReceptionEditMode] = useState(false);
    // Order-creation mode (admin/supervisor only): 'normal' or 'non_commandable'.
    // Filters which supplier products appear in the "Ajouter un produit"
    // catalogue inside the consolidation. Existing rows in the table are
    // NEVER filtered out so a previously-added now-non-commandable product
    // stays visible — only the picker is gated.
    const [orderableMode, setOrderableMode] = useState('normal');
    const [addProductQuery, setAddProductQuery] = useState('');
    const { user, hasPermission } = useAuth();
    const isPrivileged = ['admin', 'supervisor'].includes(user?.role);
    const canSendWhatsApp = hasPermission?.('orders_send_whatsapp');
    // Livreur & profils lecture seule : consultation + PDF uniquement
    const canEditCons = hasPermission?.('orders_create') || hasPermission?.('orders_edit');
    const canReceiveCons = hasPermission?.('receptions_create');
    const canSendCons = hasPermission?.('orders_send_email');
    const [whatsappDialog, setWhatsappDialog] = useState(false);
    const [invoiceImportDialog, setInvoiceImportDialog] = useState(false);
    const [aiPrefill, setAiPrefill] = useState(null);

    const fetchDetail = useCallback(async () => {
        setLoading(true);
        try {
            const [detRes, availRes] = await Promise.all([
                authFetch(`${API}/api/consolidation/detail?supplier_id=${supplierId}&week=${week}`),
                authFetch(`${API}/api/consolidation/supplier-products?supplier_id=${supplierId}`),
            ]);
            const det = await detRes.json();
            const avail = await availRes.json();
            setDetail(det);
            setProducts(det.products || []);
            setAvailableProducts(avail);
        } catch { toast.error('Erreur chargement'); }
        finally { setLoading(false); }
    }, [supplierId, week]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    const sites = detail?.sites || config?.child_sites?.map(s => ({ id: s.id, name: s.name })) || [];

    // Quantities are entered/stored/displayed in the billing_unit (Paquet,
    // Sachet, Bidon, kg, …) — i.e. the "unité de vente" — never in cartons.
    // The packaging info (e.g. "10 Sachet/Carton") is shown as a sub-label so
    // the user still knows how the supplier conditions the product, but the
    // numeric input is always the sale unit count.
    const isWeightOrVolumeUnit = (u) => {
        const x = (u || '').toLowerCase().trim();
        return x === 'kg' || x === 'g' || x === 'l' || x === 'litre' || x === 'cl' || x === 'ml';
    };

    const qtyForDisplay = (qtyBilling) => qtyBilling || 0;
    const qtyToBilling = (displayQty) => Math.max(0, parseFloat(displayQty) || 0);

    // Label shown next to each qty cell and under the product name.
    const cellUnitLabel = (p) => p.billing_unit || p.invoice_unit || '';

    // Quantites sont toujours en unite de facturation (billing_unit, ex: pieces).
    // Total: si prix par order_unit (invoice_unit == order_unit) avec colisage => qty / pkg × price
    //        sinon (prix par billing_unit) => qty × price
    const calcAmount = (qty, p) => {
        const q = qty || 0;
        const price = p.unit_price || 0;
        const pkg = p.packaging_qty || 0;
        const orderUnit = (p.order_unit || '').toLowerCase();
        const invoiceUnit = (p.invoice_unit || p.billing_unit || '').toLowerCase();
        const invoicePerOrderUnit = pkg > 1 && invoiceUnit && orderUnit && invoiceUnit === orderUnit;
        if (invoicePerOrderUnit) {
            return (q / pkg) * price;
        }
        return q * price;
    };
    const supplier = detail?.supplier;

    const updateQty = (pIdx, siteId, value) => {
        setProducts(prev => {
            const copy = [...prev];
            const p = copy[pIdx];
            // Entered value is already in billing_unit — store as-is.
            const billingQty = qtyToBilling(value);
            copy[pIdx] = {
                ...p,
                quantities: { ...p.quantities, [siteId]: billingQty },
            };
            copy[pIdx].total_qty = Object.values(copy[pIdx].quantities).reduce((a, b) => a + b, 0);
            return copy;
        });
    };

    const removeProduct = (pIdx) => {
        setProducts(prev => prev.filter((_, i) => i !== pIdx));
    };

    const addProduct = (sp) => {
        const existing = products.find(p => p.supplier_product_id === sp.supplier_product_id);
        if (existing) { toast.error('Produit deja present'); return; }
        const quantities = {};
        sites.forEach(s => { quantities[s.id] = 0; });
        setProducts(prev => [...prev, {
            supplier_product_id: sp.supplier_product_id,
            industrial_product_id: sp.industrial_product_id,
            name: sp.name,
            unit_price: sp.unit_price,
            order_unit: sp.order_unit,
            billing_unit: sp.billing_unit,
            invoice_unit: sp.invoice_unit,
            packaging_qty: sp.packaging_qty,
            sell_by_detail: sp.sell_by_detail,
            quantities,
            total_qty: 0,
        }]);
        setShowAddProduct(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await authFetch(`${API}/api/consolidation/update`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ supplier_id: supplierId, week, products }),
            });
            toast.success('Commandes mises a jour');
            fetchDetail();
        } catch { toast.error('Erreur sauvegarde'); }
        finally { setSaving(false); }
    };

    const handleSend = async () => {
        if (!window.confirm('Envoyer la commande groupee au fournisseur ?')) return;
        setSending(true);
        try {
            const res = await authFetch(`${API}/api/consolidation/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ supplier_id: supplierId, week }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail);
            toast.success(data.message);
            fetchDetail();
        } catch (e) { toast.error(e.message || 'Erreur envoi'); }
        finally { setSending(false); }
    };

    // POST a consolidated WhatsApp share. Returns the wa.me URL + the PDF
    // URL, then opens both. Each constituent order gets its status bumped
    // to confirmed (no downgrade) and an entry in whatsapp_logs[].
    const shareConsolidatedWhatsApp = async ({ phone, contact_name }) => {
        const res = await authFetch(`${API}/api/consolidation/whatsapp`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({ supplier_id: supplierId, week, phone, contact_name }),
        });
        const data = await res.json();
        if (!res.ok) {
            const e = new Error(data.detail || 'Erreur partage WhatsApp');
            e.response = { data };
            throw e;
        }
        return { data };
    };

    const handlePDF = () => {
        openAuthPdf(`${API}/api/consolidation/pdf?supplier_id=${supplierId}&week=${week}`);
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

    // Filter the "Ajouter un produit" catalogue by orderable mode (admin/supervisor only).
    // Regular users always see the commandable catalogue. 'all' mode includes
    // non-commandable products too (full catalogue access).
    const effectiveOrderableMode = isPrivileged ? orderableMode : 'normal';
    const matchOrderableMode = (sp) => (
        effectiveOrderableMode === 'all'
            ? true
            : sp.commandable !== false
    );
    const notInList = availableProducts
        .filter(matchOrderableMode)
        .filter(ap => !products.find(p => p.supplier_product_id === ap.supplier_product_id));

    // Cross-category search — matches the same UX as the orders module's
    // CategorizedProductSelectContent so users have one consistent behavior
    // for "add a product". Case- and accent-insensitive.
    const normalizeSearch = (s) => (s || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const searchNeedle = normalizeSearch(addProductQuery.trim());
    const notInListFiltered = searchNeedle
        ? notInList.filter(sp => normalizeSearch([sp.name, sp.category].filter(Boolean).join(' ')).includes(searchNeedle))
        : notInList;
    // Products matching the search that are ALREADY in the consolidation —
    // shown greyed out so the user understands why they're absent from the picker.
    const alreadyInListMatches = searchNeedle
        ? availableProducts
            .filter(ap => products.find(p => p.supplier_product_id === ap.supplier_product_id))
            .filter(sp => normalizeSearch([sp.name, sp.category].filter(Boolean).join(' ')).includes(searchNeedle))
        : [];

    return (
        <div data-testid="consolidation-detail">
            <div className="flex items-center justify-between gap-2 mb-2">
                <button onClick={onBack} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
                    <ArrowLeft className="w-3.5 h-3.5" /> Retour
                </button>
                <div className="flex items-baseline gap-2 flex-1 justify-end">
                    <h2 className="text-sm font-bold text-slate-800 truncate">{supplier?.name || 'Fournisseur'}</h2>
                    <span className="text-[11px] text-slate-400 whitespace-nowrap">Sem. {week.split('W')[1] || week} · {weekDateRange(week)}</span>
                </div>
            </div>

            {/* Cross table */}
            <div className="overflow-x-auto -mx-4 px-4 pb-2">
                <table className="w-full text-xs border-collapse min-w-[700px]">
                    <thead>
                        <tr className="bg-slate-800 text-white">
                            <th className="text-left px-2 py-2 font-semibold rounded-tl-lg w-full">Produit</th>
                            {sites.map(s => (
                                <th key={s.id} className="text-center px-0.5 py-2 font-semibold whitespace-nowrap">{s.name.replace('FE ', '')}</th>
                            ))}
                            <th className="text-center px-2 py-2 font-semibold">Total</th>
                            <th className="text-center px-2 py-2 font-semibold">P.U.</th>
                            <th className="text-center px-2 py-2 font-semibold rounded-tr-lg">Montant</th>
                            <th className="w-6"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p, pi) => {
                            // When a reception has been validated, show the received quantities
                            // (with a visual highlight when they differ from the ordered qty).
                            const showReceived = !!detail?.reception_done;
                            const displayTotalQty = showReceived ? (p.total_received_qty || 0) : (p.total_qty || 0);
                            const lineTotal = calcAmount(displayTotalQty, p);
                            return (
                                <tr key={p.supplier_product_id} className={pi % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                    <td className="px-2 py-0.5 font-medium text-slate-700 border-b border-slate-100 w-full">
                                        <div className="truncate">{p.name}</div>
                                        <span className="text-[10px] text-slate-400">
                                            {/* Always display the billing/sale unit; packaging shown as informative
                                                suffix (e.g. "Sachet (10/carton)") so the user keeps the colisage
                                                context while still entering quantities in the sale unit. */}
                                            {(p.billing_unit || p.invoice_unit || '') +
                                                (p.packaging_qty > 1 && p.order_unit
                                                    ? ` (${p.packaging_qty}/${p.order_unit})`
                                                    : '')}
                                        </span>
                                    </td>
                                    {sites.map(s => {
                                        const orderedQtyBilling = p.quantities?.[s.id] || 0;
                                        const receivedQtyBilling = p.received_quantities?.[s.id] || 0;
                                        const orderedQty = qtyForDisplay(orderedQtyBilling);
                                        const receivedQty = qtyForDisplay(receivedQtyBilling);
                                        const diff = showReceived && receivedQtyBilling !== orderedQtyBilling;
                                        const stepVal = isWeightOrVolumeUnit(p.billing_unit || p.invoice_unit) ? '0.01' : '1';
                                        return (
                                            <td key={s.id} className="px-0.5 py-0.5 text-center border-b border-slate-100">
                                                <Input
                                                    type="number"
                                                    step={stepVal}
                                                    min={0}
                                                    value={orderedQty}
                                                    disabled={!canEditCons}
                                                    onChange={e => updateQty(pi, s.id, e.target.value)}
                                                    className={`h-6 w-16 text-center text-xs mx-auto py-0 px-1 ${diff ? 'line-through text-slate-400 decoration-slate-400' : ''}`}
                                                    data-testid={`qty-${pi}-${s.id}`}
                                                    title={diff ? `Commandé : ${orderedQty} — Réceptionné : ${receivedQty}` : undefined}
                                                />
                                                {diff && (
                                                    <div className="text-[11px] font-bold text-emerald-600 tabular-nums leading-tight mt-0.5" data-testid={`received-${pi}-${s.id}`}>
                                                        {receivedQty}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className={`px-2 py-0.5 text-center border-b border-slate-100 font-bold whitespace-nowrap ${showReceived && (p.total_received_qty !== p.total_qty) ? 'text-emerald-600' : 'text-orange-600'}`}>
                                        {qtyForDisplay(displayTotalQty)}
                                        <span className="ml-1 text-[10px] font-normal text-slate-400">{cellUnitLabel(p)}</span>
                                        {showReceived && (p.total_received_qty !== p.total_qty) && (
                                            <span className="block text-[10px] font-normal text-slate-400 line-through leading-tight">
                                                {qtyForDisplay(p.total_qty || 0)} {cellUnitLabel(p)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-2 py-0.5 text-center border-b border-slate-100 text-slate-500 whitespace-nowrap">
                                        {(p.unit_price || 0).toFixed(2)}
                                    </td>
                                    <td className="px-2 py-0.5 text-center border-b border-slate-100 font-semibold text-slate-800 whitespace-nowrap" data-testid={`montant-${pi}`}>
                                        {lineTotal.toFixed(2)}
                                    </td>
                                    <td className="px-0.5 py-0.5 border-b border-slate-100">
                                        {canEditCons && (
                                        <button onClick={() => removeProduct(pi)} className="text-red-400 hover:text-red-600">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {products.length === 0 && (
                            <tr><td colSpan={sites.length + 5} className="text-center py-6 text-slate-400">Aucun produit. Ajoutez des produits ci-dessous.</td></tr>
                        )}
                    </tbody>
                    {products.length > 0 && (
                        <tfoot>
                            <tr className="bg-orange-500 text-white font-bold">
                                <td className="px-2 py-2 rounded-bl-lg">TOTAL</td>
                                {sites.map(s => {
                                    const orderedTot = products.reduce((sum, p) => sum + calcAmount(p.quantities?.[s.id] || 0, p), 0);
                                    const receivedTot = products.reduce((sum, p) => sum + calcAmount(p.received_quantities?.[s.id] || 0, p), 0);
                                    const tot = detail?.reception_done ? receivedTot : orderedTot;
                                    return (
                                        <td key={s.id} className="px-0.5 py-2 text-center whitespace-nowrap">
                                            {tot.toFixed(2)}
                                        </td>
                                    );
                                })}
                                <td className="px-2 py-2 text-center"></td>
                                <td className="px-2 py-2 text-center"></td>
                                <td className="px-2 py-2 text-center rounded-tr-lg whitespace-nowrap" data-testid="grand-total">
                                    {products.reduce((sum, p) => sum + calcAmount(detail?.reception_done ? (p.total_received_qty || 0) : (p.total_qty || 0), p), 0).toFixed(2)} EUR
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Add product */}
            {showAddProduct ? (
                <div className="mt-2 bg-white border border-slate-200 rounded-lg p-2 max-h-64 overflow-y-auto" data-testid="add-product-panel">
                    <div className="flex items-center justify-between mb-2 gap-2 sticky top-0 bg-white z-10 pt-0.5 pb-1">
                        <div className="relative flex-1 max-w-xs">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                            <input
                                type="text"
                                value={addProductQuery}
                                onChange={(e) => setAddProductQuery(e.target.value)}
                                placeholder="Rechercher…"
                                className="w-full h-7 pl-7 pr-6 text-xs rounded border border-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                                data-testid="consolidation-add-product-search"
                            />
                            {addProductQuery && (
                                <button
                                    type="button"
                                    aria-label="Effacer la recherche"
                                    onClick={() => setAddProductQuery('')}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"
                                >
                                    <X className="w-3 h-3 text-slate-400" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <OrderableModeToggle mode={orderableMode} onChange={setOrderableMode} />
                            <button onClick={() => { setShowAddProduct(false); setAddProductQuery(''); }} className="text-xs text-slate-400">Fermer</button>
                        </div>
                    </div>
                    {(() => {
                        // Group by category
                        const byCat = new Map();
                        notInListFiltered.forEach(sp => {
                            const cat = sp.category || 'Sans categorie';
                            if (!byCat.has(cat)) byCat.set(cat, []);
                            byCat.get(cat).push(sp);
                        });
                        const sortedCats = Array.from(byCat.keys()).sort((a, b) => a.localeCompare(b, 'fr'));
                        return sortedCats.map(cat => (
                            <div key={cat} className="mb-1">
                                <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50">{cat}</div>
                                {byCat.get(cat).map(sp => (
                                    <button
                                        key={sp.supplier_product_id}
                                        onClick={() => addProduct(sp)}
                                        className="w-full text-left text-xs pl-4 pr-2 py-1.5 hover:bg-slate-50 rounded flex justify-between"
                                    >
                                        <span className="text-slate-700">{sp.name}</span>
                                        <span className="text-slate-400">{sp.unit_price?.toFixed(2)} EUR</span>
                                    </button>
                                ))}
                            </div>
                        ));
                    })()}
                    {alreadyInListMatches.length > 0 && (
                        <div className="mb-1" data-testid="consolidation-already-in-list">
                            <div className="px-2 py-1 text-[10px] font-bold text-amber-600 uppercase tracking-wider bg-amber-50">Déjà dans la commande</div>
                            {alreadyInListMatches.map(sp => (
                                <div key={sp.supplier_product_id} className="w-full text-left text-xs pl-4 pr-2 py-1.5 flex justify-between text-slate-400">
                                    <span className="italic">{sp.name}</span>
                                    <span className="text-amber-600 text-[10px]">déjà présent — modifiez la ligne dans le tableau ↑</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {notInListFiltered.length === 0 && (
                        <p className="text-xs text-slate-400 text-center py-2">
                            {searchNeedle
                                ? (alreadyInListMatches.length > 0 ? 'Ce produit est déjà dans la commande (voir ci-dessus).' : 'Aucun produit trouvé')
                                : (effectiveOrderableMode === 'all'
                                    ? 'Aucun produit disponible pour ce fournisseur'
                                    : 'Tous les produits sont deja ajoutes')}
                        </p>
                    )}
                </div>
            ) : canEditCons ? (
                <Button variant="outline" size="sm" className="w-full mt-2 text-xs" onClick={() => setShowAddProduct(true)} data-testid="add-product-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un produit
                </Button>
            ) : null}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 mt-4 items-stretch">
                {canEditCons && (
                <Button variant="outline" className="flex-1 text-xs" onClick={handleSave} disabled={saving} data-testid="save-consolidation-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Sauvegarder
                </Button>
                )}
                <Button variant="outline" className="text-xs" onClick={handlePDF} data-testid="pdf-consolidation-btn">
                    <FileDown className="w-4 h-4" />
                </Button>
                <PrintButton size="default" className="text-xs" testId="print-consolidation-btn"
                    url={() => `${API}/api/consolidation/pdf?supplier_id=${supplierId}&week=${week}`} />
                {canReceiveCons && (
                <Button
                    variant="outline"
                    className="flex-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                    onClick={() => setInvoiceImportDialog(true)}
                    data-testid="cons-invoice-import-btn"
                >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Réception IA
                </Button>
                )}
                {detail?.reception_done ? (
                    canReceiveCons && (
                    <Button
                        variant="outline"
                        className="flex-1 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => { setReceptionEditMode(true); setShowReceptionDialog(true); }}
                        data-testid="correct-cons-reception-btn"
                    >
                        <PackageCheck className="w-4 h-4 mr-1" />
                        Corriger la réception
                    </Button>
                    )
                ) : (
                    <>
                        {canReceiveCons && (
                        <Button
                            variant="outline"
                            className="flex-1 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => { setReceptionEditMode(false); setShowReceptionDialog(true); }}
                            data-testid="open-cons-reception-btn"
                        >
                            <PackageCheck className="w-4 h-4 mr-1" />
                            Réceptionner
                        </Button>
                        )}
                        {canSendCons && (
                        <Button className="flex-1 text-xs bg-orange-500 hover:bg-orange-600 text-white" onClick={handleSend} disabled={sending} data-testid="send-consolidation-btn">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                            Envoyer
                        </Button>
                        )}
                        {/* Consolidated WhatsApp share. Big green button if at
                            least one supplier contact has send_whatsapp=true,
                            small discreet icon-button otherwise (still works
                            for ad-hoc manual numbers). */}
                        {canSendWhatsApp && (() => {
                            const hasOptInContact = (supplier?.contacts || []).some(
                                c => c.send_whatsapp && (c.phone || '').replace(/\D/g, '').length >= 8
                            );
                            if (hasOptInContact) {
                                return (
                                    <Button
                                        className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                        onClick={() => setWhatsappDialog(true)}
                                        data-testid="share-consolidation-whatsapp-btn"
                                    >
                                        <MessageCircle className="w-4 h-4 mr-1" />
                                        WhatsApp
                                    </Button>
                                );
                            }
                            return (
                                <button
                                    type="button"
                                    onClick={() => setWhatsappDialog(true)}
                                    className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-emerald-600 transition-colors px-2 py-1"
                                    title="Partager sur WhatsApp (aucun contact préconfiguré — saisie manuelle d'un numéro)"
                                    data-testid="share-consolidation-whatsapp-btn-small"
                                >
                                    <MessageCircle className="w-3.5 h-3.5" />
                                    WhatsApp
                                </button>
                            );
                        })()}
                    </>
                )}
            </div>

            <ConsolidatedAIImportDialog
                open={invoiceImportDialog}
                onClose={() => setInvoiceImportDialog(false)}
                supplierId={supplierId}
                siteId={sites[0]?.id}
                onExtracted={(prefill) => {
                    setAiPrefill(prefill);
                    setReceptionEditMode(false);
                    setShowReceptionDialog(true);
                }}
            />

            <ConsolidatedReceptionDialog
                open={showReceptionDialog}
                onClose={() => { setShowReceptionDialog(false); setAiPrefill(null); }}
                supplierId={supplierId}
                supplierName={supplier?.name}
                week={week}
                onCommitted={fetchDetail}
                editMode={receptionEditMode}
                aiPrefill={aiPrefill}
            />

            {/* Consolidated WhatsApp share — reuses the single-order dialog
                with a custom share function pointing at the consolidation
                endpoint. The "déjà partagée" banner is derived from the 1st
                constituent order's whatsapp_sent_at so the UI is informative
                even if subsequent orders don't have the field yet. */}
            <WhatsAppShareDialog
                open={whatsappDialog}
                onOpenChange={setWhatsappDialog}
                supplier={supplier}
                order={{
                    id: `consolidation-${supplierId}-${week}`,
                    // Aggregate "déjà partagée" hint from any order that has
                    // been WhatsApp'd this week (most recent timestamp wins).
                    whatsapp_sent_at: (detail?.orders_by_site
                        ? Object.values(detail.orders_by_site).map(o => o.whatsapp_sent_at).filter(Boolean).sort().slice(-1)[0]
                        : null
                    ),
                    whatsapp_logs: [],
                }}
                shareFn={shareConsolidatedWhatsApp}
                pdfUrl={`${API}/api/consolidation/pdf?supplier_id=${supplierId}&week=${week}`}
                pdfFilename={`commande_groupee_${week}_${supplier?.name || 'fournisseur'}.pdf`}
                shareLabel="consolidée"
                onShared={fetchDetail}
            />
        </div>
    );
}

export default function ConsolidationPage({ embedded = false }) {
    const [config, setConfig] = useState(null);
    const [week, setWeek] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [loading, setLoading] = useState(true);
    const [emailTestMode, setEmailTestMode] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [configRes, testRes] = await Promise.all([
                    authFetch(`${API}/api/consolidation/config`),
                    authFetch(`${API}/api/suppliers/email-test-mode`)
                ]);
                const data = await configRes.json();
                setConfig(data);
                setWeek(data.current_week);
                const testData = await testRes.json();
                setEmailTestMode(testData.enabled);
            } catch { toast.error('Erreur'); }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="flex justify-center items-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    return (
        <div className="max-w-5xl mx-auto" data-testid="consolidation-page">
            <div className="flex items-center justify-between gap-2 mb-2">
                {!embedded && <h1 className="text-base font-bold text-slate-900">Consolidation</h1>}
                <div className="flex items-center gap-2 flex-1 justify-end">
                    {config?.child_sites?.length > 0 && (
                        <div className="hidden sm:flex gap-1 text-[10px] text-slate-500 overflow-x-auto">
                            {config.child_sites.map(s => (
                                <span key={s.id} className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded whitespace-nowrap">{s.name.replace('FE ', '')}</span>
                            ))}
                        </div>
                    )}
                    {week && <WeekSelector week={week} onChange={w => { setWeek(w); setSelectedSupplier(null); }} />}
                </div>
            </div>

            {emailTestMode && !embedded && (
                <div className="flex items-center gap-2 px-3 py-1.5 mb-2 bg-amber-50 border border-amber-300 rounded-lg" data-testid="email-test-mode-banner">
                    <FlaskConical className="w-3.5 h-3.5 text-amber-600" />
                    <span className="text-xs font-medium text-amber-800">Mode Test actif</span>
                    <span className="text-[10px] text-amber-600 truncate">— Emails consolidés redirigés vers adresse de test</span>
                </div>
            )}

            {selectedSupplier ? (
                <DetailPanel
                    supplierId={selectedSupplier}
                    week={week}
                    onBack={() => setSelectedSupplier(null)}
                    config={config}
                />
            ) : (
                <OverviewPanel
                    week={week}
                    onSelectSupplier={setSelectedSupplier}
                />
            )}
        </div>
    );
}
